import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { inr, dt, apiError } from '../utils/format';
import { useAuth } from '../context/AuthContext';
import PayslipView from '../components/PayslipView';
import { useDataTable, TableToolbar, TableFilterRow, useRowPick, PickedChip } from '../components/DataTable';

// Org-wide payslip register. Run/finalize payroll from the Run Payroll page.
const Payslips = () => {
  const { can } = useAuth();
  const isOwner = can('*');   // only the owner may undo paid payroll
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewSlip, setViewSlip] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/employees/payslips?month=${month}`).then(({ data }) => setRows(data.payslips || []))
      .catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, [month]);

  // Deleting a payslip rewinds everything finalizing it wrote — off balance, loan
  // recovery and the month's ledger settlement — so the month can be run again.
  const del = async (p) => {
    if (!window.confirm(`Delete the ${p.month} payslip for ${p.employee?.name || `#${p.employeeId}`}?

This reverses the off balance, un-recovers the loan installment and reopens the ledger entries for that month. Payroll can then be run again.`)) return;
    try {
      const { data } = await api.delete(`/employees/payslips/${p.id}`);
      toast.success(data.message);
      setRows((rs) => rs.filter((r) => r.id !== p.id));
    } catch (e) { toast.error(apiError(e)); }
  };

  const sum = (k) => rows.reduce((s, r) => s + Number(r[k] || 0), 0);
  const dedOf = (p) => Number(p.excessOffDeduction) + Number(p.salaryAdvance) + Number(p.itemsTotal) + Number(p.loanInstallment) + Number(p.otherDeductions) - Number(p.bonus);

  const dtP = useDataTable(rows, {
    searchPlaceholder: 'Search employee, month, reference…',
    searchKeys: [(p) => p.employee?.name, 'month', 'paidVia', 'paidRef'],
    columns: [
      { key: 'employee', label: 'Employee', value: (p) => p.employee?.name, placeholder: 'Employee' },
      { key: 'branch', label: 'Branch', value: (p) => p.employee?.location?.name, type: 'select' },
      { key: 'paidVia', label: 'Paid', value: (p) => (p.paidVia || '').toUpperCase(), type: 'select' },
    ],
  });
  const pick = useRowPick();

  return (
    <div>
      <div className="section-head">
        <div className="flex gap" style={{ alignItems: 'center' }}>
          <label className="muted small">Month</label>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} style={{ width: 170 }} />
          <span className="muted small">{rows.length} payslips · {inr(sum('netPay'))} net</span>
        </div>
        <Link to="/payroll" className="btn btn-outline btn-sm">Run payroll ›</Link>
      </div>
      <TableToolbar dt={dtP} right={<PickedChip pick={pick} />} />
      {loading ? <div className="spinner" /> : (
        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr><th className="freeze-col">Employee</th><th>Month</th><th className="right">Fixed</th><th className="right">Deductions</th><th className="right">Net paid</th><th>Paid on</th>{isOwner && <th></th>}</tr>
              <TableFilterRow dt={dtP} cells={['employee', null, null, null, null, 'paidVia', ...(isOwner ? [null] : [])]} />
            </thead>
            <tbody>
              {dtP.rows.map((p) => (
                <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => setViewSlip(p)} title="View / print payslip" {...pick.markProps(p.id)}>
                  <td {...pick.cellProps(p.id)}><Link to={`/employees/${p.employeeId}`} className="link-strong" onClick={(e) => e.stopPropagation()}>{p.employee?.name || `#${p.employeeId}`}</Link></td>
                  <td><strong>{p.month}</strong></td>
                  <td className="right">{inr(p.fixedSalary)}</td>
                  <td className="right">{dedOf(p) ? `−${inr(dedOf(p))}` : '—'}</td>
                  <td className="right"><strong>{inr(p.netPay)}</strong></td>
                  <td className="muted small">{p.paidAt ? dt(p.paidAt) : '—'}</td>
                  {isOwner && (
                    <td className="right">
                      <button className="btn btn-danger btn-sm" title="Delete this payslip (owner only)"
                        onClick={(e) => { e.stopPropagation(); del(p); }}>✕</button>
                    </td>
                  )}
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={isOwner ? 7 : 6} className="empty">No payslips for {month}.</td></tr>}
            </tbody>
            {rows.length > 0 && (
              <tfoot><tr className="total-row">
                <td colSpan={2}><strong>Total</strong></td>
                <td className="right">{inr(sum('fixedSalary'))}</td><td></td>
                <td className="right"><strong>{inr(sum('netPay'))}</strong></td><td colSpan={isOwner ? 2 : 1}></td>
              </tr></tfoot>
            )}
          </table>
        </div>
      )}
      {viewSlip && <PayslipView slip={viewSlip} employee={viewSlip.employee} onClose={() => setViewSlip(null)} />}
    </div>
  );
};

export default Payslips;
